import React from "react";
import styled, { keyframes } from "styled-components";

import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  FRAME_OUTER_WIDTH,
  FRAME_OUTER_HEIGHT,
  FRAME_SLICE_TOP,
  FRAME_SLICE_LEFT,
  FRAME_BG_BLEED,
  LANDING_DRAWING_FADE_IN_MS,
  LANDING_DRAWING_FADE_OUT_MS,
  SHOWCASE_FADE_IN_MS,
  SHOWCASE_HOLD_MS,
  SHOWCASE_HOLD_OPACITY,
  SHOWCASE_MS,
} from "../../constants";
import { usePalette } from "../../contexts/PaletteContext";
import { useTool } from "../../contexts/ToolContext";
import useEggLighting from "../../hooks/useEggLighting";
import { marcTransformToCss } from "../../virtualCoords";
import FitContainer from "../FitContainer";
import OnionMarc from "../OnionMarc";

function DrawingArea({
  eggSrc,
  canvasRef,
  handlers,
  eggTransform,
  eggLights,
  eggWarmth = 0,
  eggRedshift = 0,
  marcTransform,
  align,
  authoring,
  eggSilhouetteRef,
  inputDisabled,
  // Debug-only: array of { id, mask (base64 png), bbox, searchBbox }
  // for the currently-shown scoring region's subregions. When set,
  // renders translucent magenta mask shapes + dashed search-bbox
  // outlines over the canvas so the player can see where the
  // reference's region actually lives in canvas-virtual coords.
  debugRegions,
  // Landing-mode prop. `landingDrawing` is the saved reference trace
  // (data URL) plus a `visible` flag the cycle hook toggles on a
  // timer; rendered as an <img> overlay inside the Frame at native
  // canvas-virtual size. The egg itself is never hidden in landing —
  // it tweens between marc transforms instead of fading.
  landingDrawing,
  // Speedrun-only: when set, plays the marc-completion showcase
  // overlay — opacity-only cross-fade in lockstep with the easel
  // photo's inverse cross-fade in ReferencePhoto. `{ src, marcTransform }`,
  // both snapshotted in App at trigger time so this overlay keeps
  // showing the JUST-completed marc through phase 3 even after
  // advanceMarc has flipped to the next marc. Mounted only while
  // showcasing — the keyframes animation runs once per mount, so
  // unmounting is what resets the cycle for the next marc.
  showcaseMarc,
}) {
  // Render values come in via props (eggTransform, eggLights, eggWarmth,
  // eggRedshift, marcTransform) — those are merged from reference.json and
  // author state upstream by useEffectiveReferenceValues. The `authoring`
  // bundle covers the editing surface (drag-mode toggles, setters, A/B
  // toggles) plus the small derived values that only matter when authoring
  // (marc onion src).
  const {
    lightingEnabled = true,
    eggTransformMode = false,
    setEggTransform: onEggTransformChange,
    marcOnionSrc: marcSrc,
    marcVisible = false,
    marcOpacity = 1,
    marcOpacityTransitionMs = 0,
    marcTransformMode = false,
    setMarcTransform: onMarcTransformChange,
  } = authoring ?? {};

  const eggCanvasRef = React.useRef(null);
  useEggLighting({
    canvasRef: eggCanvasRef,
    eggSrc,
    eggTransform,
    frameW: CANVAS_WIDTH,
    frameH: CANVAS_HEIGHT,
    lights: lightingEnabled ? eggLights : null,
    silhouetteRef: eggSilhouetteRef,
  });
  const { tool, size: toolSize } = useTool();
  const { activeColor } = usePalette();
  const [cursor, setCursor] = React.useState(null);

  const showHoverCircle = tool === "pen" || tool === "eraser";
  const showFillDot = tool === "fill";

  // Cursor is rendered inside FitContainer's scaled Inner, so coords need
  // to be in virtual space (canvas buffer coords), not viewport pixels.
  // Dividing by the canvas CSS display size converts.
  const updateCursor = (event) => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    setCursor({
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    });
  };

  const hideCursor = () => setCursor(null);

  const mergedHandlers = {
    ...handlers,
    onPointerEnter: (e) => {
      updateCursor(e);
      handlers.onPointerEnter?.(e);
    },
    onPointerMove: (e) => {
      updateCursor(e);
      handlers.onPointerMove?.(e);
    },
    onPointerLeave: (e) => {
      hideCursor();
      handlers.onPointerLeave?.(e);
    },
  };

  // Egg drag in author mode. Translate-only — rotation/scale/scaleX live on
  // the AuthorToolbar sliders. We measure the drawing canvas (axis-aligned,
  // identity transform) to recover the FitContainer's display scale, so
  // viewport-pixel deltas round-trip cleanly to canvas-virtual px. The egg
  // itself has its own transforms baked in, so it can't be used to measure.
  const eggDragRef = React.useRef(null);
  const handleEggPointerDown = (event) => {
    if (!eggTransformMode) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    const fitScale = rect && rect.width > 0 ? rect.width / canvas.width : 1;
    eggDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTx: eggTransform?.tx ?? 0,
      startTy: eggTransform?.ty ?? 0,
      scale: fitScale,
    };
  };
  const handleEggPointerMove = (event) => {
    const s = eggDragRef.current;
    if (!s || s.pointerId !== event.pointerId) return;
    const dx = (event.clientX - s.startX) / s.scale;
    const dy = (event.clientY - s.startY) / s.scale;
    onEggTransformChange({
      ...eggTransform,
      tx: s.startTx + dx,
      ty: s.startTy + dy,
    });
  };
  const handleEggPointerUp = (event) => {
    const s = eggDragRef.current;
    if (!s || s.pointerId !== event.pointerId) return;
    eggDragRef.current = null;
  };

  return (
    <FitContainer
      width={FRAME_OUTER_WIDTH}
      height={FRAME_OUTER_HEIGHT}
      align={align}
    >
      <BleedBg />
      <Frame>
        <EggCanvas
          ref={eggCanvasRef}
          $interactive={eggTransformMode}
          style={{
            filter: `${tintFilter(eggWarmth, eggRedshift)} ${dropShadowFilter(eggLights)}`,
          }}
          onPointerDown={eggTransformMode ? handleEggPointerDown : undefined}
          onPointerMove={eggTransformMode ? handleEggPointerMove : undefined}
          onPointerUp={eggTransformMode ? handleEggPointerUp : undefined}
          onPointerCancel={eggTransformMode ? handleEggPointerUp : undefined}
        />
        {landingDrawing?.src && (
          <LandingDrawing
            src={landingDrawing.src}
            alt=""
            draggable={false}
            $visible={!!landingDrawing.visible}
          />
        )}
        <DrawCanvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          $tool={tool}
          $passthrough={eggTransformMode}
          $inputDisabled={inputDisabled}
          {...mergedHandlers}
        />
        {showHoverCircle && cursor && (
          <HoverCircle
            $tool={tool}
            $color={activeColor}
            style={{
              left: cursor.x,
              top: cursor.y,
              width: toolSize,
              height: toolSize,
            }}
          />
        )}
        {showFillDot && cursor && (
          <FillCursor style={{ left: cursor.x, top: cursor.y }}>
            <CrossArm $side="top" />
            <CrossArm $side="bottom" />
            <CrossArm $side="left" />
            <CrossArm $side="right" />
            <FillChip $color={activeColor} />
          </FillCursor>
        )}
        {marcSrc && (
          <OnionMarc
            src={marcSrc}
            visible={marcVisible}
            opacity={marcOpacity}
            opacityTransitionMs={marcOpacityTransitionMs}
            transform={marcTransform}
            transformMode={marcTransformMode}
            onTransformChange={onMarcTransformChange}
            frameWidth={CANVAS_WIDTH}
            frameHeight={CANVAS_HEIGHT}
          />
        )}
        {showcaseMarc && (
          <ShowcaseMarcWrapper>
            <ShowcaseMarcImg
              src={showcaseMarc.src}
              alt=""
              draggable={false}
              style={{
                transform: marcTransformToCss(showcaseMarc.marcTransform),
              }}
            />
          </ShowcaseMarcWrapper>
        )}
        {debugRegions &&
          debugRegions.map((r) =>
            r.mask && r.bbox ? (
              <RegionMask
                key={`mask-${r.id}`}
                src={r.mask}
                style={{
                  left: r.bbox.x,
                  top: r.bbox.y,
                  width: r.bbox.w,
                  height: r.bbox.h,
                }}
              />
            ) : null,
          )}
        {debugRegions &&
          debugRegions.map((r) =>
            r.searchBbox ? (
              <SearchBboxOutline
                key={`bbox-${r.id}`}
                style={{
                  left: r.searchBbox.x,
                  top: r.searchBbox.y,
                  width: r.searchBbox.w,
                  height: r.searchBbox.h,
                }}
              />
            ) : null,
          )}
      </Frame>
      <FrameOverlay src="/frame.png" alt="" draggable={false} />
    </FitContainer>
  );
}

// Marc-completion showcase overlay. Three-phase opacity-only keyframes
// (fade-in / hold / fade-out) timed to SHOWCASE_MS. No transform — the
// Img sits at marcTransform from the start, occupying the same screen
// position as the OnionMarc would in author mode, so the punchline is
// pure cross-fade with the easel photo (which runs an inverse keyframes
// in lockstep). Mounted only while showcasing, so the keyframes restart
// for each marc completion.
const SHOWCASE_FADE_IN_PCT =
  (SHOWCASE_FADE_IN_MS / SHOWCASE_MS) * 100;
const SHOWCASE_HOLD_END_PCT =
  ((SHOWCASE_FADE_IN_MS + SHOWCASE_HOLD_MS) / SHOWCASE_MS) * 100;
const showcaseMarcKeyframes = keyframes`
  0% {
    opacity: 0;
  }
  ${SHOWCASE_FADE_IN_PCT}% {
    opacity: ${SHOWCASE_HOLD_OPACITY};
  }
  ${SHOWCASE_HOLD_END_PCT}% {
    opacity: ${SHOWCASE_HOLD_OPACITY};
  }
  100% {
    opacity: 0;
  }
`;

const ShowcaseMarcWrapper = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  animation: ${showcaseMarcKeyframes} ${SHOWCASE_MS}ms ease-out forwards;
`;

const ShowcaseMarcImg = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: center center;
  user-select: none;
  pointer-events: none;
`;

// Landing reel: render the saved reference trace as an <img> sized to
// the canvas-virtual frame. The trace was authored with no transform
// baked in, so it sits at native size and matches the marc-on-egg
// proportions of the underlying egg canvas. Asymmetric ease — fade-in
// is longer than fade-out so the trace appears with a "drawn it
// gradually" feel and clears quickly when its time is up.
const LandingDrawing = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity
    ${(p) => (p.$visible ? LANDING_DRAWING_FADE_IN_MS : LANDING_DRAWING_FADE_OUT_MS)}ms
    ease-out;
`;

// Orange canvas-bg + graph-paper pattern, sized slightly larger than the
// drawing surface so it tucks under the gold frame's inner edge. Rendered
// as a separate element (not a ::before on Frame) because Frame needs
// overflow: hidden to clip strokes — which would also clip an inward
// pseudo-element's outward bleed.
const BleedBg = styled.div`
  position: absolute;
  top: ${FRAME_SLICE_TOP - FRAME_BG_BLEED}px;
  left: ${FRAME_SLICE_LEFT - FRAME_BG_BLEED}px;
  width: ${CANVAS_WIDTH + FRAME_BG_BLEED * 2}px;
  height: ${CANVAS_HEIGHT + FRAME_BG_BLEED * 2}px;
  background-color: var(--canvas-bg);
  background-image:
    linear-gradient(to right, var(--pattern-color-canvas) 1px, transparent 1px),
    linear-gradient(to bottom, var(--pattern-color-canvas) 1px, transparent 1px);
  background-size: var(--pattern-size) var(--pattern-size);
  pointer-events: none;
`;

// The drawing surface. Sized to exactly CANVAS_WIDTH × CANVAS_HEIGHT and
// offset inside the FitContainer's Inner by the frame's slice thicknesses
// so it lands in the transparent center of the gold frame PNG. No
// background of its own — BleedBg sits behind and extends past it.
// Absolutely-positioned children with \`inset: 0\` fill this box cleanly,
// and overflow: hidden keeps strokes clipped to the canvas edges.
const Frame = styled.div`
  position: absolute;
  top: ${FRAME_SLICE_TOP}px;
  left: ${FRAME_SLICE_LEFT}px;
  width: ${CANVAS_WIDTH}px;
  height: ${CANVAS_HEIGHT}px;
  overflow: hidden;
`;

// Debug overlays. The mask is a base64 PNG (white-on-transparent,
// cropped to bbox); we tint it via mask-image so it reads as a visible
// magenta shape on the orange canvas bg. The dashed outline is the
// dilated search bbox — anything the player draws OUTSIDE this box
// gets zero credit toward this region.
const RegionMask = styled.div`
  position: absolute;
  pointer-events: none;
  background-color: rgba(255, 0, 255, 0.45);
  -webkit-mask-image: url(${(p) => p.src});
  mask-image: url(${(p) => p.src});
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  z-index: 5;
`;

const SearchBboxOutline = styled.div`
  position: absolute;
  pointer-events: none;
  outline: 2px dashed rgba(255, 0, 255, 0.85);
  outline-offset: -1px;
  z-index: 5;
`;

// The gold frame PNG drawn on top of the drawing surface. Fills the
// FitContainer's Inner exactly (994×1221), with pointer-events: none so
// it doesn't intercept strokes. The PNG's transparent center reveals the
// Frame beneath.
const FrameOverlay = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: ${FRAME_OUTER_WIDTH}px;
  height: ${FRAME_OUTER_HEIGHT}px;
  pointer-events: none;
  user-select: none;
  z-index: 10;
  filter: drop-shadow(0 8px 6px rgba(0, 0, 0, 0.3));
`;

// Two 0-100 author-mode sliders feed one CSS filter stack: sepia builds a
// yellow/brown tint, saturate boosts color, hue-rotate (negative deg) pulls
// the tint toward red. Warmth and redshift both contribute to all three so
// they compose linearly — warmth alone reads yellow, redshift alone reads
// red, and the two stacked read warm-red. brightness(0.85) is the un-tuned
// baseline. Inline style overrides the styled-component filter because
// they're set on the same element — that's the intent here.
export function tintFilter(warmth, redshift) {
  const brightness = "0.85";
  const w = Math.max(0, Math.min(100, warmth)) / 100;
  const r = Math.max(0, Math.min(100, redshift)) / 100;
  if (w === 0 && r === 0) return `brightness(${brightness})`;
  const sepia = (w * 0.5 + r * 0.3).toFixed(3);
  const sat = (1 + w * 0.4 + r * 0.2).toFixed(3);
  const hue = (-(w * 5 + r * 25)).toFixed(2);
  return `brightness(${brightness}) sepia(${sepia}) saturate(${sat}) hue-rotate(${hue}deg)`;
}

// Drop shadow whose offset opposes the light direction — light from the
// upper-right pushes the shadow toward lower-left, etc. CSS drop-shadow
// works on alpha edges, so it traces the rendered egg silhouette
// (including rotation/squish). At a head-on light (Lx=Ly=0) the offsets
// collapse to zero, leaving a soft symmetric halo — same look as the
// static version.
//
// Multi-light: take the intensity-weighted average direction so a strong
// key light dominates while a weak fill nudges the shadow slightly. With a
// single light this collapses to that light's direction, matching pre-
// multilight behavior exactly.
const SHADOW_DEPTH_PX = 12;
const SHADOW_BLUR_PX = 10;
const SHADOW_OPACITY = 0.35;
export function dropShadowFilter(lights) {
  let lx = 0;
  let ly = 0;
  let w = 0;
  if (lights) {
    for (const L of lights) {
      const i = L?.intensity ?? 0;
      if (i <= 0) continue;
      lx += (L?.direction?.x ?? 0) * i;
      ly += (L?.direction?.y ?? 0) * i;
      w += i;
    }
    if (w > 0) {
      lx /= w;
      ly /= w;
    }
  }
  const dx = (-lx * SHADOW_DEPTH_PX).toFixed(2);
  const dy = (-ly * SHADOW_DEPTH_PX).toFixed(2);
  return `drop-shadow(${dx}px ${dy}px ${SHADOW_BLUR_PX}px rgba(0, 0, 0, ${SHADOW_OPACITY}))`;
}

// EggCanvas displays the relit egg. The pixel buffer is exactly Frame-sized
// (CANVAS_WIDTH × CANVAS_HEIGHT), and the eggTransform is baked in by the
// relight pass — no CSS transform here. Warmth/redshift still apply as a
// filter.
const EggCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  user-select: none;
  pointer-events: ${(p) => (p.$interactive ? "auto" : "none")};
  cursor: ${(p) => (p.$interactive ? "grab" : "default")};
  outline: ${(p) => (p.$interactive ? "2px dashed var(--color-warn)" : "none")};
  outline-offset: 0px;
`;

const DrawCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
  /* Pointer-events is killed in two cases:
     - $passthrough (author egg-transform mode): clicks pass through to
       the egg below so it can be dragged.
     - $inputDisabled (speedrun celebration window): block input so the
       player can't draw onto the previous region during the swap. */
  pointer-events: ${(p) =>
    p.$passthrough || p.$inputDisabled ? "none" : "auto"};
  cursor: ${(p) => {
    if (p.$tool === "pen" || p.$tool === "eraser" || p.$tool === "fill") {
      return "none";
    }
    return "crosshair";
  }};
`;

// Per-tool cursor preview:
//   pen    — solid disk of the active color at brush size (HoverCircle)
//   eraser — dashed grey ring at brush size (HoverCircle)
//   fill   — OS crosshair + a 3px square of the active color (FillDot),
//            aseprite-style. The shape change makes "you're in fill mode"
//            unmistakable without needing the player to read the toolbar.
const HoverCircle = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 5;

  ${(p) => {
    if (p.$tool === "eraser") {
      return `
        border: 1.5px dashed var(--color-border);
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.85);
      `;
    }
    // pen — solid disk of the active color
    return `
      background: ${p.$color};
      box-shadow:
        0 0 0 1px rgba(0, 0, 0, 0.45),
        0 0 0 2px rgba(255, 255, 255, 0.7);
    `;
  }}
`;

// Custom fill cursor: four arms framing a color chip in the center.
// We hide the OS crosshair and draw our own so the chip sits inside the
// crosshair's gap (rather than under the OS arrow) and so the chip and
// arms move together — the OS cursor was visually leading the React-
// rendered chip.
const FILL_CURSOR_SIZE = 30;
const FILL_CHIP_SIZE = 9;
const FILL_ARM_LEN = (FILL_CURSOR_SIZE - FILL_CHIP_SIZE) / 2;

const FillCursor = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  width: ${FILL_CURSOR_SIZE}px;
  height: ${FILL_CURSOR_SIZE}px;
  pointer-events: none;
  z-index: 5;
`;

const CrossArm = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.85);

  ${(p) => {
    if (p.$side === "top") {
      return `top: 0; left: 50%; width: 3px; height: ${FILL_ARM_LEN}px; transform: translateX(-50%);`;
    }
    if (p.$side === "bottom") {
      return `bottom: 0; left: 50%; width: 3px; height: ${FILL_ARM_LEN}px; transform: translateX(-50%);`;
    }
    if (p.$side === "left") {
      return `left: 0; top: 50%; height: 3px; width: ${FILL_ARM_LEN}px; transform: translateY(-50%);`;
    }
    return `right: 0; top: 50%; height: 3px; width: ${FILL_ARM_LEN}px; transform: translateY(-50%);`;
  }}
`;

const FillChip = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: ${FILL_CHIP_SIZE}px;
  height: ${FILL_CHIP_SIZE}px;
  transform: translate(-50%, -50%);
  background: ${(p) => p.$color};
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.6),
    0 0 0 2px rgba(255, 255, 255, 0.75);
`;

export default DrawingArea;
